import { useGetGcssCategoriesAllQuery } from '@/api/sanity/generated';
import { singleFlight } from '@/utils/promise';
import { slugify } from '@/utils/string';
import { marked } from 'marked';
import type { CategoryListResponse } from '@/api/skippay/gcssCategories';

export type Slug = string;

export type Category = Omit<NonNullable<CategoryListResponse>[number], 'subcategories'> & {
	name: string;
	slug: Slug;
	description: string | null;
	image: string | null;
	subcategories: Array<{
		title: string;
		slug: Slug[];
		image: string | null;
	}>;
};

export type SubCategory = Category & {
	paths: Slug[];
};

type CategorySetting = {
	description: string | null;
	image: string | null;
};

const cache = new Map<number, CategorySetting>();

function escapeHtml(html: string): string {
	return html
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

marked.use({ renderer: { html: ({ text }) => escapeHtml(text) } });

export const primeGcssCategoryCache = singleFlight(runPrime);

async function runPrime(): Promise<void> {
	const categories = await useGetGcssCategoriesAllQuery.fetcher()();

	if (categories.allGcssCategory.length === 0) {
		throw new Error(
			'Sanity returned no GcssCategory rows; refusing to render every category without settings'
		);
	}

	for (const row of categories.allGcssCategory) {
		if (row.mappedCategory == null) {
			continue;
		}

		cache.set(row.mappedCategory, {
			description: row.description ? marked.parse(row.description, { async: false }) : null,
			image: row.image?.asset?.url ?? null,
		});
	}
}

export async function getCategoryDescriptionAndImage(id: number): Promise<CategorySetting | null> {
	return cache.get(id) ?? null;
}

export async function mapSubcategoriesWithDescriptionAndImage(
	category: {
		id: number;
		path: string[];
		subcategories: number[];
	},
	categoryMap: Map<number, Category['subcategories'][number]>
): Promise<Category['subcategories'][number][]> {
	const subcategories: Category['subcategories'][number][] = [];

	for (const subCategory of category.subcategories) {
		const relatedCategory = categoryMap.get(subCategory);

		if (relatedCategory) {
			const categorySetting = await getCategoryDescriptionAndImage(subCategory);

			const slug = [...category.path.map(slugify), ...relatedCategory.slug];
			subcategories.push({
				...relatedCategory,
				slug,
				image: categorySetting ? categorySetting.image : null,
			});
		}
	}

	return subcategories;
}
